/**
 * Backend VoiceLive service for server-side session management and announcement control.
 * Replaces direct Azure WebSocket connection with proper server-side control.
 */

import { logger } from '../utils/logger';

export interface VoiceLiveSessionConfig {
  conversation_id: string;
  age: number;
  story_context?: string;
  custom_instructions?: string;
}

export interface StoryAnnouncementConfig {
  conversation_id: string;
  story_id: string;
  story_title: string;
  age: number;
  announcement_duration_ms?: number;
  story_text?: string; // Optional story text for reading progress tracker initialization
  announcement_text?: string; // Age-appropriate announcement text from backend
}

export interface WelcomeAnnouncementConfig {
  conversation_id: string;
  age: number;
  include_stories?: boolean;
  announcement_duration_ms?: number;
  client_time_hour?: number; // Client's local time hour (0-23) for time-based greeting
}

export interface VoiceLiveSessionResponse {
  success: boolean;
  conversation_id: string;
  websocket_url: string;
  session_config: any;
  age_profile: any;
}

export interface AnnouncementControlResponse {
  success: boolean;
  conversation_id: string;
  message: string;
  announcement_config?: any;
  restored_config?: any;
}

class BackendVoiceLiveService {
  private baseUrl: string;
  private wsConnections: Map<string, WebSocket> = new Map();
  private voiceLiveClient: any = null;  // Reference to VoiceLiveClient for announcement control

  constructor() {
    // Use environment variable for backend URL or fallback to production URL
    console.info('process.env.REACT_APP_API_BASE_URL: '+process.env.REACT_APP_API_BASE_URL)
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://learning-companion-be-dev.data-nonprod.edgenuity.com';
  }

  setVoiceLiveClient(client: any): void {
    this.voiceLiveClient = client;
  }

  /**
   * Create a VoiceLive session with server-side management
   */
  async createSession(config: VoiceLiveSessionConfig): Promise<VoiceLiveSessionResponse> {
    try {
      logger.logVoiceLiveEvent('backend_session_create_requested', {
        conversation_id: config.conversation_id,
        age: config.age,
        has_story_context: !!config.story_context
      }, config.age, config.conversation_id);

      const response = await fetch(`${this.baseUrl}/voicelive/session/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        // Try to get detailed error message from response body
        let errorDetail = `${response.status} ${response.statusText}`;
        try {
          const errorBody = await response.json();
          if (errorBody.detail) {
            errorDetail = errorBody.detail;
          }
        } catch (e) {
          // If response body is not JSON, use status text
        }
        
        throw new Error(`Failed to create session: ${errorDetail}`);
      }

      const result: VoiceLiveSessionResponse = await response.json();

      logger.logVoiceLiveEvent('backend_session_created', {
        conversation_id: result.conversation_id,
        session_success: result.success,
        websocket_url_provided: !!result.websocket_url
      }, config.age, config.conversation_id);

      return result;

    } catch (error) {
      logger.logErrorEvent('backend_session_create_failed', error as Error, {
        conversation_id: config.conversation_id,
        age: config.age
      }, config.age, config.conversation_id);
      throw error;
    }
  }

  /**
   * Create a story session record for tracking a specific story within an existing conversation.
   * Allows multiple stories to be tracked independently within the same conversationId.
   */
  async createStorySession(config: {
    conversation_id: string;
    story_id: string;
    story_title: string;
    age: number;
  }): Promise<{
    success: boolean;
    session_id: string;
    conversation_id: string;
    story_id: string;
    story_title: string;
    message: string;
  }> {
    try {
      logger.logVoiceLiveEvent('backend_story_session_create_requested', {
        conversation_id: config.conversation_id,
        story_id: config.story_id,
        story_title: config.story_title,
        age: config.age
      }, config.age, config.conversation_id);

      const response = await fetch(`${this.baseUrl}/voicelive/session/story`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        let errorDetail = `${response.status} ${response.statusText}`;
        try {
          const errorBody = await response.json();
          if (errorBody.detail) {
            errorDetail = errorBody.detail;
          }
        } catch (e) {
          // If response body is not JSON, use status text
        }
        
        throw new Error(`Failed to create story session: ${errorDetail}`);
      }

      const result = await response.json();

      logger.logVoiceLiveEvent('backend_story_session_created', {
        conversation_id: result.conversation_id,
        session_id: result.session_id,
        story_id: result.story_id,
        story_title: result.story_title,
        session_success: result.success
      }, config.age, config.conversation_id);

      return result;

    } catch (error) {
      logger.logErrorEvent('backend_story_session_create_failed', error as Error, {
        conversation_id: config.conversation_id,
        story_id: config.story_id
      }, config.age, config.conversation_id);
      throw error;
    }
  }

  /**
   * Start welcome announcement with available stories and VAD disabled
   */
  async startWelcomeAnnouncement(config: WelcomeAnnouncementConfig): Promise<AnnouncementControlResponse> {
    try {
      logger.logVoiceLiveEvent('backend_welcome_announcement_requested', {
        conversation_id: config.conversation_id,
        age: config.age,
        include_stories: config.include_stories,
        expected_duration_ms: config.announcement_duration_ms
      }, config.age, config.conversation_id);

      const response = await fetch(`${this.baseUrl}/voicelive/announcement/welcome`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        throw new Error(`Failed to start welcome announcement: ${response.status} ${response.statusText}`);
      }

      const result: AnnouncementControlResponse = await response.json();

      logger.logVoiceLiveEvent('backend_welcome_announcement_started', {
        conversation_id: result.conversation_id,
        vad_disabled: true,
        server_controlled: true,
        welcome_message_created: true
      }, config.age, config.conversation_id);

      // Server handles announcement control - no frontend intervention needed

      return result;

    } catch (error) {
      logger.logErrorEvent('backend_welcome_announcement_failed', error as Error, {
        conversation_id: config.conversation_id,
        age: config.age
      }, config.age, config.conversation_id);
      throw error;
    }
  }

  /**
   * Start story announcement with VAD disabled to prevent interruptions
   */
  async startStoryAnnouncement(config: StoryAnnouncementConfig): Promise<AnnouncementControlResponse> {
    try {
      logger.logStoryEvent('backend_announcement_start_requested', {
        story_id: config.story_id,
        story_title: config.story_title,
        conversation_id: config.conversation_id,
        expected_duration_ms: config.announcement_duration_ms
      }, config.age, config.conversation_id);

      const response = await fetch(`${this.baseUrl}/voicelive/announcement/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        throw new Error(`Failed to start announcement: ${response.status} ${response.statusText}`);
      }

      const result: AnnouncementControlResponse = await response.json();

      logger.logStoryEvent('backend_announcement_started', {
        story_id: config.story_id,
        story_title: config.story_title,
        conversation_id: result.conversation_id,
        vad_disabled: true,
        server_controlled: true
      }, config.age, config.conversation_id);

      // Server handles announcement control - no frontend intervention needed

      return result;

    } catch (error) {
      logger.logErrorEvent('backend_announcement_start_failed', error as Error, {
        story_id: config.story_id,
        story_title: config.story_title,
        conversation_id: config.conversation_id
      }, config.age, config.conversation_id);
      throw error;
    }
  }

  /**
   * Complete story announcement and restore normal VAD
   */
  async completeStoryAnnouncement(conversationId: string, age: number): Promise<AnnouncementControlResponse> {
    try {
      logger.logStoryEvent('backend_announcement_complete_requested', {
        conversation_id: conversationId,
        restore_vad: true
      }, age, conversationId);

      const response = await fetch(`${this.baseUrl}/voicelive/announcement/complete/${conversationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to complete announcement: ${response.status} ${response.statusText}`);
      }

      const result: AnnouncementControlResponse = await response.json();

      logger.logStoryEvent('backend_announcement_completed', {
        conversation_id: result.conversation_id,
        vad_restored: true,
        ready_for_conversation: true
      }, age, conversationId);

      // Server handles announcement completion - no frontend intervention needed

      return result;

    } catch (error) {
      logger.logErrorEvent('backend_announcement_complete_failed', error as Error, {
        conversation_id: conversationId
      }, age, conversationId);
      throw error;
    }
  }

  /**
   * Get session status
   */
  async getSessionStatus(conversationId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/voicelive/session/status/${conversationId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to get session status: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.logErrorEvent('session_status_failed', error as Error, {
        conversation_id: conversationId
      });
      throw error;
    }
  }

  /**
   * Validate Azure configuration
   */
  async validateConfiguration(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/voicelive/config/validate`);
      
      if (!response.ok) {
        throw new Error(`Failed to validate config: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.logErrorEvent('config_validation_failed', error as Error);
      throw error;
    }
  }

  /**
   * Connect to backend WebSocket proxy for VoiceLive
   */
  async connectWebSocket(conversationId: string, age: number): Promise<WebSocket> {
    try {
      // Close existing connection if any
      if (this.wsConnections.has(conversationId)) {
        const existingWs = this.wsConnections.get(conversationId);
        if (existingWs && existingWs.readyState === WebSocket.OPEN) {
          existingWs.close();
        }
      }

      const wsUrl = `${this.baseUrl.replace('http', 'ws')}/voicelive/ws/${conversationId}`;
      const ws = new WebSocket(wsUrl);

      // Set up connection handlers
      ws.onopen = () => {
        logger.logVoiceLiveEvent('backend_websocket_connected', {
          conversation_id: conversationId,
          websocket_url: wsUrl
        }, age, conversationId);
      };

      ws.onclose = (event) => {
        logger.logVoiceLiveEvent('backend_websocket_disconnected', {
          conversation_id: conversationId,
          code: event.code,
          reason: event.reason
        }, age, conversationId);
        this.wsConnections.delete(conversationId);
      };

      ws.onerror = (error) => {
        logger.logErrorEvent('backend_websocket_error', error as any, {
          conversation_id: conversationId,
          websocket_url: wsUrl
        }, age, conversationId);
      };

      // Store connection
      this.wsConnections.set(conversationId, ws);

      return ws;

    } catch (error) {
      logger.logErrorEvent('backend_websocket_connect_failed', error as Error, {
        conversation_id: conversationId
      }, age, conversationId);
      throw error;
    }
  }

  /**
   * Disconnect WebSocket for conversation
   */
  disconnectWebSocket(conversationId: string): void {
    const ws = this.wsConnections.get(conversationId);
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close();
      this.wsConnections.delete(conversationId);
    }
  }

  /**
   * Send message through WebSocket
   */
  sendWebSocketMessage(conversationId: string, message: any): boolean {
    const ws = this.wsConnections.get(conversationId);
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
      return true;
    }
    return false;
  }

  /**
   * Get troubleshooting information
   */
  async getTroubleshootingInfo(issueType: string, age?: number): Promise<any> {
    try {
      let url = `${this.baseUrl}/voicelive/troubleshoot/${issueType}`;
      if (age) {
        url += `?age=${age}`;
      }

      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to get troubleshooting info: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.logErrorEvent('troubleshooting_fetch_failed', error as Error, {
        issue_type: issueType,
        age: age
      });
      throw error;
    }
  }

  /**
   * Submit quiz answer for validation
   */
  async submitQuizAnswer(request: {
    conversation_id: string;
    question_id: number;
    selected_answer: string;
    time_taken_seconds?: number;
  }): Promise<{
    correct: boolean;
    correct_answer: string;
    explanation: string | null;
    encouragement: string;
    question_number: number;
    total_questions: number;
  }> {
    try {
      logger.logVoiceLiveEvent('quiz_answer_submitted', {
        conversation_id: request.conversation_id,
        question_id: request.question_id,
        time_taken: request.time_taken_seconds
      }, undefined, request.conversation_id);

      const response = await fetch(`${this.baseUrl}/quiz/validate-answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        let errorDetail = `${response.status} ${response.statusText}`;
        try {
          const errorBody = await response.json();
          if (errorBody.detail) {
            errorDetail = errorBody.detail;
          }
        } catch (e) {
          // Use status text if body is not JSON
        }
        throw new Error(`Failed to validate answer: ${errorDetail}`);
      }

      const result = await response.json();

      logger.logVoiceLiveEvent('quiz_answer_validated', {
        conversation_id: request.conversation_id,
        question_id: request.question_id,
        correct: result.correct,
        question_number: result.question_number,
        total_questions: result.total_questions
      }, undefined, request.conversation_id);

      return result;
    } catch (error) {
      logger.logErrorEvent('quiz_answer_validation_failed', error as Error, {
        conversation_id: request.conversation_id,
        question_id: request.question_id
      });
      throw error;
    }
  }

  /**
   * Get quiz session progress
   */
  async getQuizProgress(conversationId: string): Promise<{
    total_questions: number;
    questions_answered: number;
    correct_answers: number;
    incorrect_answers: number;
    completion_percentage: number;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/quiz/session-progress/${conversationId}`);

      if (!response.ok) {
        throw new Error(`Failed to get quiz progress: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.logErrorEvent('quiz_progress_fetch_failed', error as Error, {
        conversation_id: conversationId
      });
      throw error;
    }
  }
}

// Export singleton instance
export const backendVoiceLiveService = new BackendVoiceLiveService();